/* ============================================================
 * src/screens/pending/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-pending.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. Channel-context surfaces (Encoder
// helper, Channel performance, Recent publishes, Featured channel) now
// bind to `useChannelList()`. Decorative tables backed by no public
// query family (provider spend, automation logs, batch ops, version
// history) render honest empty states until the real surfaces land.
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { useChannelList } from '../../hooks/useChannelList';
import { fmtDur, ago, money, statusLabels } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { Wordmark } from '../../layouts/Shell';
import { LineChart } from '../insights/_bundle';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Pending screens, states & shared modals (v2 gap-fill)
   ============================================================ */

/* ---------- 5.11 Encoder Setup Helper ---------- */
export function EncoderHelper({ id }: { id?: string }) {
  // T318 — wired channel context. Honest skeleton/empty while the
  // wired list resolves or the route id has no match.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  const { toast } = useToast();
  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />;
  }
  const c = { id: real._id, _id: real._id, name: real.name, slug: real.slug };
  const [type, setType] = useState('ffmpeg');
  const ffmpeg = `ffmpeg -re -i entrada.mp4 \\
  -c:v libx264 -preset veryfast -b:v 4500k -maxrate 4500k \\
  -bufsize 9000k -g 60 -c:a aac -b:a 128k -ar 44100 \\
  -f flv rtmps://ingest.circa.tv/live/••••••••1234`;
  const steps = {
    obs: ['Abre OBS Studio → Ajustes → Emisión.', 'Servicio: "Personalizado". Servidor: rtmps://ingest.circa.tv/live.', 'Clave de retransmisión: pega tu credencial revelada.', 'Ajustes → Salida → Tasa de bits de video: 4500 kbps.', 'Resolución: 1920×1080 a 30 fps. Aplica e inicia la transmisión.'],
    wirecast: ['Abre Wirecast → Output → Output Settings.', 'Destino: RTMP Server. URL: rtmps://ingest.circa.tv/live.', 'Stream: pega tu credencial revelada.', 'Encoding: 1080p30, 4500 kbps. Inicia el stream.'],
    browser: ['Abre el codificador web de Circa desde tu navegador.', 'Permite cámara/pantalla cuando se solicite.', 'Selecciona el canal y pulsa "Conectar".', 'La señal se valida automáticamente antes de salir al aire.'],
  };
  return (
    <div>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name, href: '#/channels/' + c.id }, { label: 'Salir al aire', href: '#/channels/' + c.id + '/go-live' }, { label: 'Codificador' }]}
        title="Configuración del codificador" description={`${c.name} — instrucciones para conectar tu codificador.`} />
      <div style={{ marginBottom: 20 }}><SegmentedControl value={type} onChange={setType} options={[{ value: 'ffmpeg', label: 'Línea de comandos' }, { value: 'obs', label: 'OBS' }, { value: 'wirecast', label: 'Wirecast' }, { value: 'browser', label: 'Navegador' }]} /></div>
      <div className="circa-cols-8-4">
        <Panel title={type === 'ffmpeg' ? 'Comando Línea de comandos' : 'Pasos'}>
          {type === 'ffmpeg' ? (
            <div>
              <div style={{ position: 'relative' }}>
                <pre style={{ margin: 0, background: 'var(--forest-a)', color: '#e6f4ee', borderRadius: 16, padding: 20, fontFamily: 'var(--font-mono)', fontSize: 13, lineHeight: 1.6, overflowX: 'auto' }}>{ffmpeg}</pre>
                <div style={{ position: 'absolute', top: 12, right: 12 }}><Btn size="sm" variant="onDark" icon="copy" onClick={() => toast({ variant: 'success', message: 'Comando copiado.' })}>Copiar</Btn></div>
              </div>
              <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--muted-slate)' }}>Reemplaza la credencial enmascarada con el valor revelado desde “Administrar credenciales”.</p>
            </div>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {steps[type].map((s, i) => <li key={i} style={{ fontSize: 15, lineHeight: 1.5 }}><b style={{ fontFamily: 'var(--font-display)' }}>Paso {i + 1}.</b> {s}</li>)}
            </ol>
          )}
        </Panel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Parámetros recomendados">
            {[['Protocolo', 'RTMPS'], ['Tasa de bits', '4500 kbps'], ['Resolución', '1920×1080'], ['Fotogramas', '30 fps'], ['Audio', 'AAC 128 kbps']].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--lightest-gray)', fontSize: 14 }}><span style={{ color: 'var(--muted-slate)' }}>{k}</span><span className="t-mono" style={{ textTransform: 'none', fontSize: 13 }}>{v}</span></div>
            ))}
          </Panel>
          <Btn variant="solid" full iconRight="arrowRight" onClick={() => navigate('/channels/' + c.id + '/go-live')}>Volver a salir al aire</Btn>
        </div>
      </div>
    </div>
  );
}

/* ---------- 5.14 Channel Performance ---------- */
export function ChannelPerformance({ id }: { id?: string }) {
  // T318 — wired channel context. Outcome tile counts are NOT exposed
  // by a public per-channel rollup query in this milestone; until that
  // surface lands, the rollups render '—' rather than fabricated counts.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />;
  }
  const c = { id: real._id, _id: real._id, name: real.name };
  const outcomes: Array<[string, string | number, string?]> = [
    ['Pendiente', '—'],
    ['Disparado', '—'],
    ['Inserción observada', '—', 'good'],
    ['Sin inserción', '—', 'warn'],
    ['Error de tubería', '—', 'bad'],
    ['Tiempo agotado', '—', 'bad'],
    ['Desconocido', '—'],
    ['Histórico', '—'],
  ];
  return (
    <div>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name, href: '#/channels/' + c.id }, { label: 'Rendimiento' }]}
        title="Rendimiento" description="Entrega de cortes de anuncio, ordenada por total en las últimas 24 horas."
        actions={<Select value="Últimas 24 h" onChange={() => {}} options={['Últimas 24 h', 'Últimos 7 d', 'Últimos 30 d']} />} />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Cortes totales" value="—" /><StatCard label="Completados" value="—" /><StatCard label="Fallidos" value="—" /><StatCard label="Canales" value="1" />
      </div>
      <Panel title="Desglose de resultados" style={{ marginBottom: 24 }}>
        <div className="circa-grid-4">
          {outcomes.map(([l, v, tone]) => <StatCard key={l} label={l} value={v} tone={tone as any} help={l === 'Inserción observada' ? '(1) No significa que un espectador vio el anuncio. (2) Es una señal de disparo. (3) Mide la entrega, no la audiencia.' : undefined} />)}
        </div>
      </Panel>
      <Panel title="Tasa de inserción en el tiempo">
        <EmptyState icon="chart" title="Sin datos" body="No hay métricas de inserción para mostrar." />
      </Panel>
    </div>
  );
}

/* ---------- 9.5 Provider Spend ---------- */
export function ProviderSpend() {
  return (
    <div>
      <PageHead title="Gasto por proveedor" description="Uso de API por proveedor y tendencias de costo." actions={<Select value="Últimos 30 d" onChange={() => {}} options={['Últimos 7 d', 'Últimos 30 d', 'Año actual']} />} />
      <FreshnessBanner show message="Los datos de uso provienen de resúmenes diarios y se actualizan cada pocas horas." />
      {/* T318 — no public provider-spend rollup query in this
          milestone. Until it lands, the surface renders an honest
          empty state rather than fabricated cost cards. */}
      <EmptyState
        icon="dollar"
        title="Sin datos de gasto"
        body="No hay rollups de uso de proveedores disponibles en este momento."
      />
    </div>
  );
}

/* ---------- 10.6 Automation Detail ---------- */
export function AutomationDetail({ id }: { id?: string }) {
  // T318 — wired automation context. Renders an honest skeleton/empty
  // when the route id has no real automation row.
  const wiredAuts = useQuery(api.automations.list, {}) as
    | Array<{ _id: string; name?: string; enabled?: boolean; runs?: number; success?: number; lastRanAt?: number }>
    | undefined;
  const wiredA = wiredAuts?.find(x => x._id === id);
  const { toast } = useToast();
  if (wiredAuts === undefined) {
    return <Skeleton h={420} />;
  }
  if (wiredA === undefined) {
    return <EmptyState icon="alertTriangle" title="Automatización no encontrada" body="No se localizó la automatización solicitada." />;
  }
  const a = {
    id: wiredA._id,
    name: wiredA.name ?? '—',
    enabled: wiredA.enabled ?? false,
    runs: wiredA.runs ?? 0,
    success: wiredA.success ?? 0,
    last: wiredA.lastRanAt ?? null,
  };
  return (
    <div>
      <PageHead crumbs={[{ label: 'Automatizaciones', href: '#/automations' }, { label: a.name }]} title={a.name}
        actions={<><Toggle on={a.enabled} onChange={() => {}} /><Btn variant="ghost" icon="edit">Editar</Btn>
          <Btn variant="ghost" iconRight="fileText" onClick={() => navigate('/automations/' + a.id + '/logs')}>Ver registro</Btn></>} />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Ejecuciones totales" value={a.runs} /><StatCard label="Tasa de éxito" value={a.runs ? a.success + '%' : '—'} tone={a.runs ? 'good' : undefined} />
        <StatCard label="Última ejecución" value={a.last ? ago(a.last) : '—'} /><StatCard label="Duración media" value="1.2 s" />
      </div>
      <Panel title="Ejecuciones recientes" pad={0}>
        {/* T318 — no public per-automation execution-log query yet. */}
        <div style={{ padding: 24 }}>
          <EmptyState icon="bolt" title={a.runs ? 'Sin registros recientes' : 'Sin ejecuciones'} body={a.runs ? 'No hay ejecuciones recientes para mostrar.' : 'Esta automatización aún no se ha disparado. Actívala para empezar.'} />
        </div>
      </Panel>
    </div>
  );
}

/* ---------- 10.7 Automation Logs ---------- */
export function AutomationLogs({ id }: { id?: string }) {
  // T318 — wired automation lookup; per-execution logs render honest empty.
  const wiredAuts = useQuery(api.automations.list, {}) as
    | Array<{ _id: string; name?: string }>
    | undefined;
  const wiredA = wiredAuts?.find(x => x._id === id);
  const [open, setOpen] = useState<number | null>(null);
  if (wiredAuts === undefined) {
    return <Skeleton h={420} />;
  }
  if (wiredA === undefined) {
    return <EmptyState icon="alertTriangle" title="Automatización no encontrada" body="No se localizó la automatización solicitada." />;
  }
  const a = { id: wiredA._id, name: wiredA.name ?? '—' };
  return (
    <div>
      <PageHead crumbs={[{ label: 'Automatizaciones', href: '#/automations' }, { label: a.name, href: '#/automations/' + a.id }, { label: 'Registro' }]}
        title={'Historial: ' + a.name} actions={<Select value="Todos" onChange={() => {}} options={['Todos', 'Éxito', 'Parcial', 'Fallido']} />} />
      <Panel pad={0}>
        {/* T318 — no public per-automation execution-log query. */}
        <div style={{ padding: 24 }}>
          <EmptyState icon="bolt" title="Sin registros" body="No hay registros de ejecuciones para mostrar." />
        </div>
        {/* keep the open setter referenced */}
        {void open}
      </Panel>
    </div>
  );
}

/* ---------- 16.1 Manage Captions ---------- */
export function ManageCaptions({ id }: { id?: string }) {
  // T318 — wired asset context.
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    id ? { assetId: id as any } : 'skip',
  ) as { _id?: string; title?: string } | undefined | null;
  const { toast } = useToast();
  if (id && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (id && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  const a = { id: aDoc?._id ?? '', _id: aDoc?._id ?? '', title: aDoc?.title ?? '—' };
  const [tracks, setTracks] = useState([
    { lang: 'ES', label: 'Español', fmt: 'WebVTT', def: true, auto: false },
    { lang: 'EN', label: 'English', fmt: 'SRT', def: false, auto: true },
  ]);
  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title, href: '#/library/' + a.id }, { label: 'Subtítulos' }]}
        title="Subtítulos" description={`${a.title} — administra las pistas de subtítulos.`} actions={<Btn variant="ghost" icon="upload" onClick={() => navigate('/library/captions/bulk')}>Carga masiva</Btn>} />
      <div className="circa-cols-8-4">
        <Panel title="Pistas de subtítulos" pad={0}>
          {tracks.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < tracks.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <Pill tone="neutral" size="sm">{t.lang}</Pill>
              <span style={{ flex: 1, fontSize: 14 }}>{t.label}</span>
              {t.auto && <Pill tone="info" size="sm">Automática</Pill>}
              <Pill tone="neutral" size="sm">{t.fmt}</Pill>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted-slate)' }}>Predet. <Toggle on={t.def} onChange={() => setTracks(ts => ts.map((x, j) => ({ ...x, def: j === i })))} /></span>
              <Dropdown align="right" trigger={<Btn variant="ghost" icon="moreH" size="sm" />} items={[{ label: 'Vista previa', icon: 'eye' }, { label: 'Descargar', icon: 'download' }, { divider: true }, { label: 'Eliminar', icon: 'trash', danger: true, onClick: () => setTracks(ts => ts.filter((_, j) => j !== i)) }]} />
            </div>
          ))}
          {tracks.length === 0 && <div style={{ padding: 24 }}><EmptyState icon="fileText" title="Sin pistas de subtítulos" body="Sube una o genera subtítulos desde el análisis automático." /></div>}
        </Panel>
        <Panel title="Añadir">
          <Btn variant="ghost" full icon="upload" style={{ marginBottom: 10 }} onClick={() => { setTracks(ts => [...ts, { lang: 'FR', label: 'Français', fmt: 'WebVTT', def: false, auto: false }]); toast({ variant: 'success', message: 'Pista añadida.' }); }}>Subir pista</Btn>
          <Btn variant="ghost" full icon="sparkles" onClick={() => { setTracks(ts => [...ts, { lang: 'PT', label: 'Português (auto)', fmt: 'WebVTT', def: false, auto: true }]); toast({ variant: 'success', message: 'Subtítulos generados desde el análisis.' }); }}>Generar desde análisis</Btn>
        </Panel>
      </div>
    </div>
  );
}

/* ---------- 16.2 Bulk Caption Upload ---------- */
export function BulkCaptions() {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: 'Subtítulos en masa' }]} title="Carga masiva de subtítulos" description="Empareja archivos de subtítulos con videos por nombre o título." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Panel title="1 · Subir archivos">
          <div onClick={() => setStep(2)} style={{ border: '2px dashed var(--border-cool)', borderRadius: 22, padding: '40px 24px', textAlign: 'center', cursor: 'pointer' }}>
            <Icon name="uploadCloud" size={40} color="var(--muted-slate)" />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, margin: '12px 0 4px' }}>Suelta varios archivos a la vez</div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)' }}>Aceptamos .scc, .srt y .vtt. Intentaremos emparejar cada uno con un video.</p>
          </div>
        </Panel>
        {step === 2 && (
          <Panel title="2 · Emparejar" pad={0}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1fr 1fr', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
              <span>Archivo</span><span>Video sugerido</span><span>Confianza</span><span>Idioma</span></div>
            {/* T318 — file → asset matching has no public Convex
                surface in this milestone. Honest empty state. */}
            <div style={{ padding: 24 }}>
              <EmptyState
                icon="upload"
                title="Sin emparejamientos"
                body="Sube archivos para iniciar el emparejamiento con tus videos."
              />
            </div>
            <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}><Btn variant="solid" onClick={() => toast({ variant: 'success', message: 'Subtítulos emparejados y subidos.' })}>Confirmar emparejamiento</Btn></div>
          </Panel>
        )}
      </div>
    </div>
  );
}

/* ---------- 6.9 Edit Metadata ---------- */
export function EditMetadata({ id }: { id?: string }) {
  // T318 — wired asset context.
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    id ? { assetId: id as any } : 'skip',
  ) as { _id?: string; title?: string; tags?: string[] } | undefined | null;
  const m = useMutationWithToast();
  if (id && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (id && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  const a = { id: aDoc?._id ?? '', _id: aDoc?._id ?? '', title: aDoc?.title ?? '—', tags: aDoc?.tags ?? [] };
  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title, href: '#/library/' + a.id }, { label: 'Metadatos' }]} title="Editar metadatos" />
      <Panel pad={28}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Identidad</div>
          <Field label="Título"><Input value={a.title} onChange={() => {}} /></Field>
          <Field label="Descripción"><Textarea value="" onChange={() => {}} placeholder="Describe este contenido…" /></Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Clasificación</div>
          <Field label="Tipo de contenido"><SegmentedControl value="VOD" onChange={() => {}} options={[{ value: 'VOD', label: 'VOD' }, { value: 'Filler', label: 'Relleno' }, { value: 'House Ad', label: 'Anuncio' }, { value: 'Bumper', label: 'Bumper' }]} /></Field>
          <div className="circa-grid-2-tight">
            <Field label="Categoría"><Input value="Noticias" onChange={() => {}} /></Field>
            <Field label="Clasificación por edad"><Input value="TP" onChange={() => {}} /></Field>
          </div>
          <Field label="Etiquetas"><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{a.tags.map(t => <CircaTag key={t} onRemove={() => {}}>{t}</CircaTag>)}</div></Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Metadatos de programa (guía / EPG)</div>
          <div className="circa-grid-2-tight">
            <Field label="Serie"><Input value="" onChange={() => {}} /></Field>
            <Field label="Categoría IAB"><Input value="IAB12" onChange={() => {}} /></Field>
            <Field label="Temporada"><Input value="" onChange={() => {}} /></Field>
            <Field label="Episodio"><Input value="" onChange={() => {}} /></Field>
          </div>
          <div><Btn variant="solid" onClick={() => { m(() => {}); navigate('/library/' + a.id); }}>Guardar metadatos</Btn></div>
        </div>
      </Panel>
    </div>
  );
}

/* ---------- 13.2 Editorial Sync ---------- */
export function EditorialSync() {
  return (
    <AdminGate>
      <PageHead title="Sincronización editorial" description="Estado en vivo del flujo de revisión editorial." />
      <FreshnessBanner show message="El estado del flujo editorial puede ir ligeramente atrasado." />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Aprobaciones (24 h)" value="42" /><StatCard label="Aplicadas (24 h)" value="40" tone="good" /><StatCard label="Reintentando" value="2" tone="warn" /><StatCard label="Cola de envío" value="0" tone="good" />
      </div>
      <Panel title="Ejecuciones del flujo" pad={0} style={{ marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr 1fr 0.8fr', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Inicio</span><span>Correlación</span><span>Operación</span><span>Estado</span><span>Dur.</span></div>
        {/* T318 — no public editorial-flow execution list in this
            milestone; honest empty until the surface lands. */}
        <div style={{ padding: 24 }}>
          <EmptyState
            icon="refresh"
            title="Sin ejecuciones recientes"
            body="No hay ejecuciones del flujo editorial para mostrar."
          />
        </div>
      </Panel>
      <ConnectionTestTool />
    </AdminGate>
  );
}

/* ---------- 13.4 Connection test tool ---------- */
export function ConnectionTestTool() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  return (
    <Panel pad={20}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <Icon name={open ? 'chevronD' : 'chevronR'} size={16} color="var(--muted-slate)" />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>Probar la conexión segura</span>
      </div>
      {open && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--body-muted)' }}>Úsalo para verificar que tu CMS firma las solicitudes correctamente, sin exponer la ruta de producción a tráfico de prueba.</p>
          <Field label="Cuerpo de ejemplo"><Textarea value="" onChange={() => {}} rows={3} placeholder='{"event":"asset.created"}' /></Field>
          <div className="circa-grid-2-tight">
            <Field label="Firma (hex)"><Input value="" onChange={() => {}} placeholder="a8f2…" /></Field>
            <Field label="Marca de tiempo (ms)"><Input value={String(Date.now())} onChange={() => {}} /></Field>
          </div>
          <div><Btn variant="solid" onClick={() => setResult(Math.random() > 0.4 ? 'ok' : 'bad')}>Verificar firma</Btn></div>
          {result && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 14, borderRadius: 14, background: result === 'ok' ? 'var(--pale-green)' : 'rgba(220,38,38,0.06)' }}>
              <Icon name={result === 'ok' ? 'checkCircle' : 'alertCircle'} size={18} color={result === 'ok' ? 'var(--good)' : 'var(--bad)'} />
              <span style={{ fontSize: 14 }}>{result === 'ok' ? 'Verificada. La conexión está firmada correctamente.' : 'Firma no coincide. Revisa el secreto compartido y la marca de tiempo.'}</span>
            </div>
          )}
          <p style={{ margin: 0, fontSize: 12, color: 'var(--muted-slate)' }}>Las solicitudes deben firmarse dentro de 300 segundos de su llegada. Las más antiguas se rechazan para prevenir ataques de repetición.</p>
        </div>
      )}
    </Panel>
  );
}

/* ---------- 13.3 Editorial item review ---------- */
export function EditorialItemReview({ itemId }: { itemId?: string }) {
  // T318 — wired asset lookup; the canonical wired surface is at
  // src/screens/editorial/EditorialItemReview.tsx — this bundle entry
  // mirrors its wiring for the prototype-style copy.
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    itemId ? { assetId: itemId as any } : 'skip',
  ) as { _id?: string; title?: string; durationMs?: number; addedAtMs?: number } | undefined | null;
  const { toast } = useToast();
  const [comment, setComment] = useState('');
  if (itemId && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (itemId && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Elemento no encontrado" body="No se localizó el elemento solicitado." />;
  }
  const a = { id: aDoc?._id ?? '', title: aDoc?.title ?? '—', durationMs: aDoc?.durationMs ?? 0, addedAt: aDoc?.addedAtMs ?? Date.now() };
  return (
    <div>
      <PageHead crumbs={[{ label: 'Bandeja editorial', href: '#/editorial-inbox' }, { label: a.title }]} title={a.title} />
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Vista previa" pad={0}><div style={{ padding: 20 }}><Thumb radius={14}><div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="play" size={26} color="#fff" /></div></Thumb></div></Panel>
          <Panel title="Descripción"><p style={{ margin: 0, fontSize: 16, color: 'var(--body-muted)', lineHeight: 1.6 }}>Contenido recibido desde una fuente conectada. Revisa la descripción y los metadatos del feed antes de aceptar o rechazar.</p></Panel>
          <Panel title="Comentarios de revisión">
            <div style={{ display: 'flex', gap: 10 }}><Input value={comment} onChange={setComment} placeholder="Añade una nota…" /><Btn variant="ghost" onClick={() => { setComment(''); toast({ variant: 'success', message: 'Comentario añadido.' }); }}>Enviar</Btn></div>
          </Panel>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Metadatos del feed">
            {[['Fuente', 'Reuters Video'], ['Duración', fmtDur(a.durationMs)], ['Título original', a.title], ['ID original', 'rt_' + a.id], ['Recibido', ago(a.addedAt)]].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--lightest-gray)', fontSize: 13 }}><span style={{ color: 'var(--muted-slate)' }}>{k}</span><span style={{ textAlign: 'right' }}>{v}</span></div>
            ))}
          </Panel>
          <Panel title="Decisión">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Btn variant="solid" full icon="check" onClick={() => { toast({ variant: 'success', message: 'Aceptado y añadido a la biblioteca.' }); navigate('/editorial-inbox'); }}>Aceptar</Btn>
              <Btn variant="danger" full onClick={() => { toast({ variant: 'info', message: 'Elemento rechazado.' }); navigate('/editorial-inbox'); }}>Rechazar</Btn>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

/* ---------- 14.3 Recent Publishes ledger ---------- */
export function RecentPublishes() {
  // T318 — no public per-tenant publication-ledger query in this
  // milestone. The filter chrome remains but the row table is honest
  // empty until the surface lands.
  const [filter, setFilter] = useState('Todas');
  const [detail, setDetail] = useState<unknown>(null);
  const outMap: Record<string, [string, string]> = { good: ['Exitoso', 'good'], warn: ['Reintentando', 'warn'], bad: ['Fallido', 'bad'] };
  const shown: Array<{ i: number; ch: string; v: string; hash: string; outcome: string; project: string; lat: number }> = [];
  return (
    <div>
      <PageHead crumbs={[{ label: 'Destinos', href: '#/destinations' }, { label: 'Publicaciones' }]} title="Publicaciones recientes" description="Registro de entregas a destinos, identificado por huella de contenido." />
      <Panel pad={16} style={{ marginBottom: 20 }}><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {['Todas', 'Exitoso', 'Reintentando', 'Fallido'].map(f => <button key={f} onClick={() => setFilter(f)} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontFamily: 'var(--font-sans)', background: filter === f ? 'var(--ink-muted)' : 'transparent', color: filter === f ? '#fff' : 'var(--slate)', borderColor: filter === f ? 'var(--ink-muted)' : 'var(--border-cool)' }}>{f}</button>)}
      </div></Panel>
      <Panel pad={0}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 0.7fr 1.2fr 1fr 0.8fr 90px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Hora</span><span>Canal</span><span>Versión</span><span>Huella</span><span>Resultado</span><span>Proyecto</span><span></span></div>
        {/* T318 — honest empty until the public publication-ledger
            query lands. */}
        <div style={{ padding: 24 }}>
          <EmptyState
            icon="send"
            title="Sin publicaciones recientes"
            body="No hay publicaciones para mostrar con el filtro seleccionado."
          />
        </div>
        {void shown}
      </Panel>
      <p style={{ margin: '14px 4px', fontSize: 12, color: 'var(--muted-slate)' }}>Cada publicación se identifica por huella de contenido. Si reintentas, no publicamos dos veces.</p>
      <SlideOver open={!!detail} onClose={() => setDetail(null)} title="Detalle de publicación" width={460}>
        {detail !== null ? <div style={{ fontSize: 14 }}>—</div> : null}
      </SlideOver>
    </div>
  );
}

/* ---------- 2.8 First-run empty workspace ---------- */
export function FirstRun() {
  return (
    <div style={{ maxWidth: 760, margin: '40px auto' }}>
      <Panel pad={40}>
        <div style={{ textAlign: 'center' }}>
          <Icon name="tv" size={48} color="var(--muted-slate)" />
          <h1 style={{ margin: '16px 0 8px', fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400 }}>Configura tu primer canal.</h1>
          <p style={{ margin: '0 auto 28px', fontSize: 18, color: 'var(--body-muted)', maxWidth: 480 }}>Un canal es una transmisión 24×7 a la que publicas contenido. Crea uno para empezar.</p>
        </div>
        <div className="circa-grid-3" style={{ marginBottom: 28 }}>
          {[['film', 'Trae tu video', 'Sube o importa contenido a tu biblioteca.'], ['calendar', 'Programa lo que sale al aire', 'Arma una parrilla de programación.'], ['radio', 'Sal al aire', 'Transmite a YouTube o a tu propio reproductor.']].map(([ic, t, d]) => (
            <div key={t} style={{ border: '1px solid var(--lightest-gray)', borderRadius: 16, padding: 18 }}>
              <Icon name={ic} size={22} color="var(--deep-green)" />
              <div style={{ fontWeight: 500, fontSize: 15, margin: '10px 0 4px' }}>{t}</div>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.45 }}>{d}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Btn variant="solid" iconRight="arrowRight" onClick={() => navigate('/channels/new')}>Crear un canal</Btn>
          <Btn variant="ghost" onClick={() => navigate('/')}>Ir al panel</Btn>
        </div>
      </Panel>
    </div>
  );
}

/* ---------- 2.9 / 2.10 Account states ---------- */
function TakeoverState({ icon, tone, title, body, primary, secondary }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--snow)' }}>
      <Panel pad={32} style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <Icon name={icon} size={36} color={TONES[tone].fg} />
        <h1 style={{ margin: '16px 0 8px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>{title}</h1>
        <p style={{ margin: '0 0 24px', fontSize: 15, color: 'var(--body-muted)', lineHeight: 1.5 }}>{body}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>{primary}{secondary}</div>
      </Panel>
    </div>
  );
}
export function AccountLocked() {
  return <TakeoverState icon="alertCircle" tone="bad" title="Tu cuenta está suspendida"
    body="Un administrador suspendió tu acceso. Contacta a tu administrador para restaurarlo."
    primary={<Btn variant="solid" as="a" href="mailto:lucia@canalcirca.tv">Contactar al administrador</Btn>}
    secondary={<Btn variant="ghost" onClick={() => navigate('/sign-in')}>Cerrar sesión</Btn>} />;
}
export function SessionExpired() {
  return <TakeoverState icon="clock" tone="warn" title="Tu sesión terminó"
    body="Por tu seguridad, cerramos tu sesión. Inicia sesión de nuevo para continuar."
    primary={<Btn variant="solid" iconRight="arrowRight" onClick={() => navigate('/sign-in')}>Iniciar sesión</Btn>} />;
}

/* ---------- 17.1 Operator HTML Dashboard (light, restyled) ---------- */
export function OperatorDashboard() {
  // T318 — wired channel list for the fleet panel. Headline tiles
  // remain '—' until real per-tenant rollup queries land.
  const operDashboardRows = useChannelList();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', padding: '32px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}><Wordmark size={26} /><Pill tone="neutral">Panel del operador</Pill></div>
        <Btn variant="ghost" icon="externalLink" onClick={() => navigate('/')}>Abrir aplicación</Btn>
      </div>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Canales al aire" value="—" /><StatCard label="Nodos listos" value="—" /><StatCard label="Alertas" value="—" /><StatCard label="Capacidad" value="—" />
      </div>
      <div className="circa-grid-2">
        <Panel title="Estado de la red" pad={0}>
          {(operDashboardRows ?? []).slice(0, 6).map((c, i) => (
            <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: i < Math.min((operDashboardRows ?? []).length, 6) - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <Pill tone={c.backendStatus === 'running' ? 'live' : c.backendStatus === 'error' ? 'bad' : 'neutral'} size="sm" dot={c.backendStatus === 'running'}>{c.backendStatus}</Pill>
              <span style={{ flex: 1, fontSize: 14 }}>{c.name}</span>
              <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{c.uptime}%</span>
            </div>
          ))}
        </Panel>
        <Panel title="Tareas en segundo plano" pad={0}>
          {['Resumen diario', 'Verificación de señal', 'Retención', 'Limpieza', 'Enriquecimiento'].map((t, i) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: i < 4 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <span style={{ flex: 1, fontSize: 14 }}>{t}</span><Pill tone="good" size="sm">OK</Pill><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>hace {i + 1}m</span>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

/* ---------- 5.18 Asset Picker modal ---------- */
export function AssetPickerModal({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick?: (id: string) => void }) {
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState('');
  // T318 — wired to api.assetQueries.getReadyAssets. Honest skeleton
  // while the list resolves; honest empty when no ready asset matches.
  const ready = useQuery(api.assetQueries.getReadyAssets, {}) as
    | Array<{ _id: string; title?: string; durationMs?: number; assetType?: string }>
    | undefined;
  const list = (ready ?? []).filter((a) => !q || (a.title ?? '').toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal open={open} onClose={onClose} title="Elegir contenido" width={760}
      footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn variant="solid" disabled={!sel} onClick={() => { onPick && onPick(sel); onClose(); }}>Usar este contenido</Btn></>}>
      <div style={{ marginBottom: 16 }}><Input value={q} onChange={setQ} icon="search" placeholder="Buscar por título" /></div>
      <div className="circa-grid-3">
        {ready === undefined ? (
          <SkeletonList rows={3} />
        ) : list.length === 0 ? (
          <EmptyState icon="film" title="Sin contenido listo" body="No hay videos listos para elegir." />
        ) : (
          list.slice(0, 9).map((a) => (
            <div key={a._id} onClick={() => setSel(a._id)} style={{ cursor: 'pointer', border: `2px solid ${sel === a._id ? 'var(--ink-muted)' : 'transparent'}`, borderRadius: 14, overflow: 'hidden', background: 'var(--snow)' }}>
              <Thumb radius={0} label={a.assetType ?? ''} />
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title ?? '—'}</div>
                <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>{a.durationMs !== undefined ? fmtDur(a.durationMs) : '—'}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

/* ---------- 5.3 Destinations Wizard ---------- */
export function DestinationsWizard({ open, onClose }) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [dest, setDest] = useState([]);
  useEffect(() => { if (open) { setStep(1); setDest([]); } }, [open]);
  const toggle = v => setDest(d => d.includes(v) ? d.filter(x => x !== v) : [...d, v]);
  const cards = [['youtube', 'tv', 'YouTube Live', 'Transmite a un canal de YouTube. Mejor para alcance.'], ['own', 'globe', 'Tu propio reproductor', 'Entrega a un reproductor o app personalizada.'], ['both', 'layers', 'Ambos', 'Publica a YouTube y a tu propio reproductor.']];
  return (
    <Modal open={open} onClose={onClose} title="Destinos del canal" width={640}
      footer={<><Btn variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : onClose()}>{step > 1 ? 'Atrás' : 'Cancelar'}</Btn>
        <Btn variant="solid" disabled={step === 1 && dest.length === 0} onClick={() => step < 3 ? setStep(step + 1) : (onClose(), toast({ variant: 'success', message: 'Destinos configurados.' }))}>{step < 3 ? 'Continuar' : 'Guardar destinos'}</Btn></>}>
      <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 16 }}>Paso {step} de 3</div>
      {step === 1 && <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>Elige al menos un destino.</h3>
        {cards.map(([id, ic, t, d]) => (
          <button key={id} onClick={() => toggle(id)} style={{ display: 'flex', gap: 14, alignItems: 'center', textAlign: 'left', border: `1px solid ${dest.includes(id) ? 'var(--deep-green)' : 'var(--border-cool)'}`, background: dest.includes(id) ? 'var(--pale-green)' : 'transparent', borderRadius: 22, padding: 18, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
            <Icon name={ic} size={22} color={dest.includes(id) ? 'var(--deep-green)' : 'var(--muted-slate)'} />
            <div><div style={{ fontWeight: 500, fontSize: 15 }}>{t}</div><div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{d}</div></div>
          </button>
        ))}
      </div>}
      {step === 2 && <div><h3 style={{ margin: '0 0 16px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>Credenciales del destino</h3>
        <Field label="Clave del destino"><Input value="" onChange={() => {}} placeholder="Pega tu clave de YouTube" /></Field></div>}
      {step === 3 && <div><h3 style={{ margin: '0 0 16px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>Confirmar</h3>
        <p style={{ fontSize: 14, color: 'var(--body-muted)' }}>Destinos seleccionados: {dest.map(d => cards.find(c => c[0] === d)[2]).join(', ') || '—'}.</p></div>}
    </Modal>
  );
}

/* ---------- 6.6 Batch Pattern Wizard ---------- */
export function BatchPatternWizard({ open, onClose }) {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  useEffect(() => { if (open) setStep(1); }, [open]);
  return (
    <Modal open={open} onClose={onClose} title="Aplicar plantilla en masa" width={720}
      footer={<><Btn variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : onClose()}>{step > 1 ? 'Atrás' : 'Cancelar'}</Btn>
        <Btn variant="solid" onClick={() => step < 3 ? setStep(step + 1) : (onClose(), toast({ variant: 'success', message: 'Trabajo en cola.' }))}>{step < 3 ? 'Continuar' : 'Iniciar trabajo'}</Btn></>}>
      <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 16 }}>Paso {step} de 3</div>
      {step === 1 && <div><h3 style={{ margin: '0 0 16px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>Elige una plantilla</h3>
        <div className="circa-grid-3">{['Solo pre-roll', 'Cada 15 min', 'Pre + post'].map((t, i) => <button key={t} style={{ border: `1px solid ${i === 0 ? 'var(--deep-green)' : 'var(--border-cool)'}`, background: i === 0 ? 'var(--pale-green)' : 'transparent', borderRadius: 16, padding: 16, cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-sans)' }}><div style={{ fontWeight: 500 }}>{t}</div><div style={{ height: 24, background: 'var(--snow)', borderRadius: 6, marginTop: 8 }} /></button>)}</div></div>}
      {step === 2 && <div><h3 style={{ margin: '0 0 16px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>Elige los videos</h3>
        {/* T318 — honest empty until the picker binds to the wired
            asset list with selection state. */}
        <EmptyState icon="film" title="Sin selección" body="Abre el selector de contenido para elegir videos." />
        </div>}
      {step === 3 && <div><h3 style={{ margin: '0 0 12px', fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>Vista previa</h3><p style={{ fontSize: 14, color: 'var(--body-muted)' }}>Se aplicará la plantilla a 5 videos. Puedes revisar el progreso en Operaciones masivas.</p></div>}
    </Modal>
  );
}

/* ---------- 6.7 Replace file modal ---------- */
export function ReplaceFileModal({ open, onClose }) {
  const { toast } = useToast();
  return (
    <Modal open={open} onClose={onClose} title="Reemplazar archivo" width={480}
      footer={<><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn variant="solid" onClick={() => { onClose(); toast({ variant: 'success', message: 'Archivo reemplazado. Se tomó una versión.' }); }}>Reemplazar</Btn></>}>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--body-muted)', lineHeight: 1.5 }}>Reemplazar conserva todos los metadatos, cortes y referencias en programaciones. El archivo anterior se mueve a la papelera.</p>
      <div style={{ border: '2px dashed var(--border-cool)', borderRadius: 16, padding: '32px 16px', textAlign: 'center' }}>
        <Icon name="uploadCloud" size={36} color="var(--muted-slate)" /><div style={{ fontSize: 14, marginTop: 10 }}>Arrastra el nuevo archivo aquí</div>
      </div>
    </Modal>
  );
}

/* ---------- 6.8 Move to trash modal ---------- */
export function TrashModal({ open, onClose, count = 1 }) {
  const { toast } = useToast();
  return (
    <ConfirmModal open={open} onClose={onClose} title="Mover a la papelera" danger typed={count >= 5 ? 'ELIMINAR' : null}
      body={`${count} ${count === 1 ? 'video se moverá' : 'videos se moverán'} a la papelera. Las programaciones que los referencian mostrarán huecos donde estaban.`}
      confirmLabel="Mover a la papelera" onConfirm={() => toast({ variant: 'success', message: 'Movido a la papelera.', action: { label: 'Deshacer', onClick: () => {} } })} />
  );
}

/* ---------- 7.6 Schedule Validation drawer ---------- */
export function ScheduleValidationDrawer({ open, onClose }) {
  const groups = [
    ['Errores', 'bad', [['Hueco de 4 min sin contenido a las 14:20', true], ['Falta tarjeta de espera al final', false]]],
    ['Advertencias', 'warn', [['Dos cortes de anuncio seguidos a las 16:00', true]]],
    ['Buenas prácticas', 'info', [['Considera añadir un bumper de identidad cada 2 h', false]]],
  ];
  return (
    <SlideOver open={open} onClose={onClose} title="Validación" width={440} footer={<Btn variant="ghost" icon="refresh" full>Volver a validar</Btn>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {groups.map(([title, tone, items]) => (
          <div key={title}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}><Pill tone={tone} size="sm">{title}</Pill><span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{items.length}</span></div>
            {items.map(([txt, jump], i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
                <Icon name={tone === 'bad' ? 'alertCircle' : tone === 'warn' ? 'alertTriangle' : 'info'} size={16} color={TONES[tone].fg} style={{ marginTop: 1 }} />
                <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4 }}>{txt}</span>
                {jump && <Btn size="sm" variant="text">Ir</Btn>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </SlideOver>
  );
}

/* ---------- 10.11 Version History detail modal ---------- */
export function VersionDetailModal({ open, onClose, entity = 'Canal' }: { open: boolean; onClose: () => void; entity?: string }) {
  // T318 — wired version list via api.versionHistory.list (workspace
  // scope). Honest skeleton/empty until the per-entity surface lands.
  const versions = useQuery(api.versionHistory.list, {}) as
    | Array<{ _id: string; createdAtMs?: number; actorName?: string; actorInitials?: string; entityKind?: string; summary?: string }>
    | undefined;
  return (
    <Modal open={open} onClose={onClose} title={'Historial — ' + entity} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {versions === undefined ? (
          <SkeletonList rows={5} />
        ) : versions.length === 0 ? (
          <EmptyState icon="clock" title="Sin versiones" body="No hay versiones registradas para mostrar." />
        ) : (
          versions.slice(0, 5).map((v, i) => (
            <div key={v._id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 0', borderBottom: i < Math.min(versions.length, 5) - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', textTransform: 'none', width: 130 }}>{v.createdAtMs !== undefined ? new Date(v.createdAtMs).toLocaleDateString('es-MX') : '—'}</span>
              <Avatar name={v.actorName ?? '—'} initials={v.actorInitials ?? '?'} size={26} />
              <Pill tone="neutral" size="sm">{v.entityKind ?? '—'}</Pill>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--body-muted)' }}>{v.summary ?? '—'}</span>
              <Btn size="sm" variant="text">Ver diff</Btn>
              <Btn size="sm" variant="ghost" onClick={() => { onClose(); }}>Restaurar</Btn>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

/* ---------- 12.9 Delivery operations (NEW, admin HTTP-only ops) ---------- */
function OpRow({ o, last, onRun }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', borderBottom: '1px solid var(--lightest-gray)' }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: o.danger ? 'rgba(220,38,38,0.06)' : 'var(--snow)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={o.icon} size={18} color={o.danger ? 'var(--bad)' : 'var(--slate)'} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>{o.label}</span>
          {o.danger && <Pill tone="bad" size="sm">Irreversible</Pill>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted-slate)', marginTop: 2 }}>{o.desc}</div>
        <div className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', textTransform: 'none', marginTop: 6 }}>Última ejecución: {last} · {o.endpoint}</div>
      </div>
      <Btn size="sm" variant={o.danger ? 'danger' : 'ghost'} icon={o.danger ? null : 'arrowRight'} onClick={() => onRun(o)}>Ejecutar</Btn>
    </div>
  );
}
export function DeliveryOps() {
  const m = useMutationWithToast();
  const { toast } = useToast();
  const [op, setOp] = useState<any>(null);
  const [target, setTarget] = useState('');
  // T318 — wired option lists for the modal's `Select`. Member/channel
  // identity is sourced from real queries so an operator never picks a
  // fabricated target.
  const opMembers = useQuery(api.organizations.listMembers, {}) as
    | Array<{ _id: string; name?: string }>
    | undefined;
  const opChannels = useChannelList();
  const opTargets =
    op?.id === 'disable' || op?.id === 'promote'
      ? opMembers === undefined
        ? undefined
        : opMembers.map((x) => x.name ?? '—')
      : opChannels === undefined
        ? undefined
        : opChannels.map((x) => x.name);
  const edge = [
    { id: 'drain', icon: 'power', label: 'Vaciar punto de entrega', desc: 'Vacía un punto de entrega antes de retirarlo del servicio.', typed: 'mx-central-1', danger: true, endpoint: 'entrega/vaciar-punto', needsTarget: true },
    { id: 'redeploy', icon: 'refresh', label: 'Forzar reinicio', desc: 'Reinicia y vuelve a publicar el servicio en un punto de entrega.', typed: 'mx-central-1', danger: true, endpoint: 'entrega/reiniciar', needsTarget: true },
    { id: 'fleet', icon: 'server', label: 'Estado de la red', desc: 'Consulta el estado en vivo de toda la red de entrega.', typed: null, danger: false, endpoint: 'entrega/estado' },
    { id: 'rotate', icon: 'key', label: 'Rotar clave de entrada', desc: 'Genera una nueva clave de entrada para un canal.', typed: 'rotar', danger: true, endpoint: 'entrega/rotar-clave', needsTarget: true },
  ];
  const accounts = [
    { id: 'disable', icon: 'lock', label: 'Deshabilitar usuario', desc: 'Suspende inmediatamente el acceso de un miembro.', typed: 'deshabilitar', danger: true, endpoint: 'usuarios/deshabilitar', needsTarget: true },
    { id: 'promote', icon: 'users', label: 'Cambiar rol de usuario', desc: 'Eleva o reduce el rol de un miembro.', typed: 'cambiar', danger: false, endpoint: 'usuarios/cambiar-rol', needsTarget: true },
  ];
  const lasts = { drain: 'hace 2 d', redeploy: 'hace 6 h', fleet: 'hace 3 min', rotate: 'hace 1 d', disable: 'nunca', promote: 'hace 4 d' };
  const run = (o) => { if (o.typed) { setOp(o); setTarget(''); } else m(() => {}, { success: o.label + ': consulta completada.' }); };
  const log = [
    ['18:42:07', 'estado-flota', 'lucia@canalcirca.tv', 'ok'],
    ['16:09:51', 'rotar-credencial · noticias-24', 'lucia@canalcirca.tv', 'ok'],
    ['11:20:33', 'redesplegar · us-east-2', 'diego@canalcirca.tv', 'ok'],
    ['09:14:02', 'drenar-nodo · sa-east-1', 'lucia@canalcirca.tv', 'fallo'],
  ];
  return (
    <AdminGate>
      <PageHead crumbs={[{ label: 'Configuración', href: '#/settings' }, { label: 'Avanzado' }, { label: 'Operaciones de entrega' }]}
        title="Operaciones de entrega" description="Acciones operativas de borde y de cuentas. Cada una se aplica en producción, requiere confirmación escrita y queda registrada."
        actions={<AuditTrailLink onClick={() => navigate('/settings/advanced/audit')} />} />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Nodos en línea" value="4 / 6" icon="server" tone="good" />
        <StatCard label="Capacidad en uso" value="62%" icon="gauge" />
        <StatCard label="Operaciones hoy" value="11" icon="bolt" />
        <StatCard label="Última operación" value="hace 3 min" icon="clock" />
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '14px 16px', background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 22, marginBottom: 24, fontSize: 13, color: 'var(--body-muted)' }}>
        <Icon name="alertTriangle" size={16} color="var(--bad)" style={{ marginTop: 1 }} />
        Estas acciones afectan la entrega en producción. No se ejecutan de forma optimista: el resultado se confirma contra el servicio antes de marcarse como exitoso.
      </div>
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Red de entrega" pad={0}>
            {edge.map(o => <OpRow key={o.id} o={o} last={lasts[o.id]} onRun={run} />)}
          </Panel>
          <Panel title="Cuentas y acceso" pad={0}>
            {accounts.map(o => <OpRow key={o.id} o={o} last={lasts[o.id]} onRun={run} />)}
          </Panel>
        </div>
        {/* deep-forest-green operator console (was purple) */}
        <div style={{ background: 'linear-gradient(160deg, var(--forest-a), var(--forest-b))', borderRadius: 22, padding: 22, color: '#fff', position: 'sticky', top: 80 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--pale-green)' }}>REGISTRO DE OPERACIONES</span>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--live)', animation: 'circa-pulse 1.4s infinite' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {log.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 12, borderBottom: i < log.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none' }}>
                <span className="t-mono" style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'none', whiteSpace: 'nowrap' }}>{l[0]}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="t-mono" style={{ fontSize: 11, textTransform: 'none', color: '#fff', wordBreak: 'break-word' }}>{l[1]}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{l[2]}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: l[3] === 'ok' ? '#7ee2b8' : '#ffb4a8' }}>{l[3] === 'ok' ? 'OK' : 'FALLO'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Modal open={!!op} onClose={() => setOp(null)} title={op ? op.label : ''} width={480}
        footer={<><Btn variant="ghost" onClick={() => setOp(null)}>Cancelar</Btn>
          <Btn variant={op && op.danger ? 'danger' : 'solid'} disabled={op && (target !== op.typed)} onClick={() => { m(() => {}, { success: op.label + ' ejecutada.' }); setOp(null); }}>Ejecutar operación</Btn></>}>
        {op && <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', textTransform: 'none' }}>POST /api/admin/{op.endpoint}</div>
          {op.needsTarget && <Field label={op.id === 'disable' || op.id === 'promote' ? 'Miembro' : 'Nodo / canal'}>
            {/* T318 — operator targets are sourced from the wired
                memberships and channel lists; honest empty option until
                both queries resolve. */}
            <Select value="" onChange={() => {}} full options={['Selecciona…', ...(opTargets ?? []).map((x) => x)]} />
          </Field>}
          <p style={{ margin: 0, fontSize: 14, color: 'var(--body-muted)', lineHeight: 1.5 }}>{op.desc} Esta operación se registra en auditoría.</p>
          <Field label={<span>Escribe <b style={{ fontFamily: 'var(--font-mono)' }}>{op.typed}</b> para confirmar</span>}>
            <Input value={target} onChange={setTarget} placeholder={op.typed} />
          </Field>
        </div>}
      </Modal>
    </AdminGate>
  );
}

/* ---------- 13.5 Embedded records + viewer preview (read-only) ---------- */
export function ViewerPreview() {
  const { toast } = useToast();
  // T318 — wired channel list. The featured channel is the first
  // running/ad_break row; honest skeleton/empty while resolving.
  const viewerRows = useChannelList();
  const featured = viewerRows?.find(c => c.backendStatus === 'running' || c.backendStatus === 'ad_break') ?? viewerRows?.[0];
  if (viewerRows === undefined) {
    return <Skeleton h={420} />;
  }
  if (featured === undefined) {
    return <EmptyState icon="tv" title="Sin canales" body="No hay canales para mostrar en este espacio de trabajo." />;
  }
  const embed = `<iframe src="https://circa.tv/embed/${featured.slug}"\n  width="640" height="360" allowfullscreen></iframe>`;
  return (
    <div>
      <PageHead title="Vista del espectador" description="Cómo se ven y suenan tus canales para la audiencia. Solo lectura — estos registros se administran en el sistema de contenido."
        actions={<Pill tone="neutral" icon="lock">Solo lectura</Pill>} />

      {/* Featured: deep-forest-green media band with player chrome + embed (was purple) */}
      <div className="circa-cols-8-4" style={{ marginBottom: 24 }}>
        <div style={{ background: 'linear-gradient(150deg, var(--forest-a), var(--forest-b))', borderRadius: 22, padding: 20 }}>
          <div style={{ position: 'relative' }}>
            <Thumb radius={14} live={featured.backendStatus === 'running'}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="play" size={26} color="#fff" /></div>
            </Thumb>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 500, color: '#fff' }}>{featured.name}</div>
              <div className="t-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', textTransform: 'none', marginTop: 4 }}>circa.tv/v/{featured.slug}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="onDark" icon="externalLink" onClick={() => toast({ variant: 'info', message: 'Abriendo reproductor público…' })}>Abrir reproductor</Btn>
              <Btn size="sm" variant="onDarkSolid" icon="send">Compartir</Btn>
            </div>
          </div>
        </div>
        <Panel title="Insertar reproductor">
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--body-muted)' }}>Pega este fragmento en tu sitio para insertar el canal en vivo.</p>
          <div style={{ position: 'relative' }}>
            <pre style={{ margin: 0, background: 'var(--forest-a)', color: '#e6f4ee', borderRadius: 14, padding: '16px 14px', fontFamily: 'var(--font-mono)', fontSize: 11.5, lineHeight: 1.6, overflowX: 'auto' }}>{embed}</pre>
            <div style={{ position: 'absolute', top: 10, right: 10 }}><Btn size="sm" variant="onDark" icon="copy" onClick={() => toast({ variant: 'success', message: 'Fragmento copiado.' })} /></div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[['Estado', featured.backendStatus === 'running' ? 'En vivo' : 'Fuera del aire'], ['Latencia', '—'], ['Calidad', '—']].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: 'var(--muted-slate)' }}>{k}</span><span>{v}</span></div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Public channels as soft-stone product cards */}
      <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 12 }}>CANALES PÚBLICOS</div>
      <div className="circa-grid-3" style={{ marginBottom: 28 }}>
        {viewerRows.slice(0, 6).map(c => {
          const onAir = c.backendStatus === 'running' || c.backendStatus === 'ad_break';
          return (
            <div key={c._id} style={{ background: 'var(--soft-stone)', borderRadius: 22, overflow: 'hidden' }}>
              <Thumb radius={0} live={c.backendStatus === 'running'} label={onAir ? '' : 'Tarjeta de espera'} />
              <div style={{ padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{c.name}</span>
                  <Pill tone={c.backendStatus === 'running' ? 'live' : c.backendStatus === 'ad_break' ? 'warn' : 'neutral'} size="sm">{c.backendStatus}</Pill>
                </div>
                <div className="t-mono" style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'none', margin: '6px 0 12px' }}>circa.tv/v/{c.slug}</div>
                <div style={{ height: 1, background: 'rgba(0,0,0,0.06)', marginBottom: 12 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--slate)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="eye" size={13} />—</span>
                  <Btn size="sm" variant="text" icon="externalLink">Abrir</Btn>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="circa-grid-2" style={{ marginBottom: 24 }}>
        <Panel title="Versiones de audio" pad={0} right={<Pill tone="neutral" size="sm">Solo lectura</Pill>}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 0.7fr 90px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
            <span>Versión</span><span>Formato</span><span>Idioma</span><span>Estado</span></div>
          {[['Estéreo 256 kbps', 'AAC', 'ES', 'good'], ['Mono 96 kbps (pódcast)', 'MP3', 'ES', 'good'], ['Audiodescripción', 'AAC', 'ES', 'warn']].map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 0.7fr 90px', padding: '13px 20px', alignItems: 'center', borderBottom: i < 2 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="mic" size={15} color="var(--muted-slate)" />{r[0]}</span>
              <Pill tone="neutral" size="sm">{r[1]}</Pill><span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{r[2]}</span>
              <Pill tone={r[3]} size="sm">{r[3] === 'good' ? 'Disponible' : 'Procesando'}</Pill>
            </div>
          ))}
        </Panel>
        <Panel title="Canales de pódcast" pad={0} right={<Pill tone="neutral" size="sm">Solo lectura</Pill>}>
          {[['Noticias en audio', 142, 'noticias-audio'], ['Documental — la serie', 28, 'documental-serie']].map((p, i) => (
            <div key={p[0]} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 20px', borderBottom: i < 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--pale-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="radio" size={18} color="var(--deep-green)" /></div>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{p[0]}</div><div className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', textTransform: 'none', marginTop: 2 }}>{p[1]} episodios · /{p[2]}</div></div>
              <Btn size="sm" variant="text" icon="externalLink">Feed RSS</Btn>
            </div>
          ))}
        </Panel>
      </div>

      <Panel title="Registros incorporados" pad={0} right={<HelpDot text="Estos registros provienen del sistema de contenido (CMS). Se muestran aquí solo para inspección y no se editan desde Circa." />}>
        <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.6fr 1fr 110px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Tipo</span><span>Registro</span><span>Actualizado</span><span></span></div>
        {[['Feed de canal', 'noticias-24 · guía 24 h', 'hace 3 min'], ['Versión de audio', 'ritmo-latino · estéreo', 'hace 1 h'], ['Canal de pódcast', 'noticias-audio · RSS', 'hace 2 h'], ['Feed de canal', 'documental · guía 18 h', 'hace 5 h']].map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.6fr 1fr 110px', padding: '13px 20px', alignItems: 'center', borderBottom: i < 3 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
            <Pill tone="neutral" size="sm">{r[0]}</Pill>
            <span className="t-mono" style={{ fontSize: 12, textTransform: 'none' }}>{r[1]}</span>
            <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{r[2]}</span>
            <Btn size="sm" variant="text" icon="externalLink">Abrir</Btn>
          </div>
        ))}
        <div style={{ padding: '14px 20px', fontSize: 12, color: 'var(--muted-slate)', background: 'var(--snow)' }}>Estos registros se administran en el sistema de contenido y no se editan desde aquí.</div>
      </Panel>
    </div>
  );
}

/* ---------- 15.1 Analyse configure modal ---------- */
export function AnalyseConfigModal({ open, onClose }) {
  const { toast } = useToast();
  const [sens, setSens] = useState(0.3);
  const [mode, setMode] = useState('enrich');
  const [caps, setCaps] = useState(true);
  const [kf, setKf] = useState(true);
  const label = sens <= 0.15 ? 'Conservador' : sens <= 0.4 ? 'Balanceado' : sens <= 0.7 ? 'Agresivo' : 'Máximo';
  const eta = mode === 'full' ? '≈ 8–12 min' : '≈ 3–5 min';
  return (
    <Modal open={open} onClose={onClose} title="Configurar análisis" width={500}
      footer={<><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginRight: 'auto', alignSelf: 'center' }}>TIEMPO ESTIMADO {eta}</span><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn variant="solid" icon="sparkles" onClick={() => { onClose(); toast({ variant: 'success', message: 'Configuración guardada. ' + eta + '.' }); }}>Guardar y analizar</Btn></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Sensibilidad de escena</span>
            <Pill tone="green" size="sm">{label} · {sens.toFixed(2)}</Pill>
          </div>
          <input type="range" min="0" max="1" step="0.05" value={sens} onChange={e => setSens(parseFloat(e.target.value))} style={{ width: '100%', accentColor: 'var(--deep-green)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }} className="t-mono">
            {['Conservador', 'Balanceado', 'Agresivo', 'Máximo'].map(l => <span key={l} style={{ fontSize: 9, color: 'var(--muted-slate)' }}>{l}</span>)}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted-slate)' }}>Mayor sensibilidad encuentra más escenas, pero puede fragmentar transiciones sutiles.</p>
        </div>
        <Field label="Modo">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[['enrich', 'Enriquecimiento', 'Transcripción, subtítulos y vectores semánticos. Recomendado para VOD con búsqueda.'], ['full', 'Completo', 'Todo lo anterior más fotogramas clave y segmentación de audio. Recomendado antes de publicar a múltiples plataformas.']].map(([id, t, d]) => (
              <button key={id} onClick={() => setMode(id)} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left', border: `1px solid ${mode === id ? 'var(--deep-green)' : 'var(--border-cool)'}`, background: mode === id ? 'var(--pale-green)' : 'transparent', borderRadius: 14, padding: 14, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${mode === id ? 'var(--deep-green)' : 'var(--border-cool)'}`, marginTop: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{mode === id && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--deep-green)' }} />}</span>
                <span><span style={{ fontWeight: 500, fontSize: 14, display: 'block' }}>{t}</span><span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{d}</span></span>
              </button>
            ))}
          </div>
        </Field>
        <div style={{ borderTop: '1px solid var(--lightest-gray)', paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>OPCIONES</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div><div style={{ fontSize: 14, fontWeight: 500 }}>Generar subtítulos automáticos</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Añade una pista WebVTT automática al video.</div></div>
            <Toggle on={caps} onChange={setCaps} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: mode === 'full' ? 1 : 0.5 }}>
            <div><div style={{ fontSize: 14, fontWeight: 500 }}>Extraer fotogramas clave</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Toma un fotograma en el punto medio de cada escena. Solo en modo Completo.</div></div>
            <Toggle on={mode === 'full' && kf} onChange={setKf} disabled={mode !== 'full'} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

